// helix-exception: Helix has no semantic token for these hues (Figma needs-input orange, highlight yellow/amber, categorical teal/cyan); primitive ramps with the identical colour are used (F-11)
import type { OrderComparatorSummary, OrderStatusResponse } from '@/utils/DatabaseOperations'

/**
 * Present the RAW backend `order.status` token to clinicians (title-cased for readability) instead
 * of a curated clinician vocabulary — with ONE exception: `draft` reads "Not submitted". The
 * workspace badge, filter option, chip, and status sort key all read `getWorkspaceStatusDisplay`, so
 * they stay in agreement. Empty/null status → 'Unknown'.
 *
 * Per-status chip STYLING is returned as literal Tailwind bg/text utility classes (applied via the
 * Badge's `className`, which tailwind-merge layers over the base theme) — NOT Flowbite `color` prop
 * keys. The Flowbite Badge color-theme classes live in `flowbite-react`'s compiled output, which the
 * Tailwind v4 content globs do NOT scan, so those themed colors get purged and render UNSTYLED.
 * Literal classes here are seen by Tailwind (this file is under ./src) and always generated.
 * Distinct hue per stage. Unmapped/empty → neutral gray.
 */
const statusBadgeClasses: Record<string, string> = {
  // Backend OrderStatus enum values (DatabaseOperations.ts).
  draft: 'bg-warning-100 dark:bg-gray-900 text-text-fg-warning dark:text-warning-100', // not yet submitted — needs attention
  pending: 'bg-brand-100 dark:bg-gray-800 text-brand-800 dark:text-brand-400', // order placed, awaiting processing
  submitted: 'bg-info-100 dark:bg-gray-900 text-info-600 dark:text-info-100', // submitted to the lab
  accessioned: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200', // sample received / accessioned
  complete: 'bg-success-100 dark:bg-gray-900 text-text-fg-success-strong dark:text-success-100', // results available
  reanalysis: 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200', // re-analysis results
  cancelled: 'bg-background-tertiary dark:bg-gray-800 text-text-body dark:text-gray-400', // terminal / inactive
}

const UNKNOWN_BADGE_CLASS = 'bg-background-tertiary dark:bg-gray-800 text-text-body dark:text-gray-400'

/** Title-case a raw status token for display, e.g. "not_submitted" → "Not Submitted". */
function titleCaseStatus(raw: string): string {
  return raw
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ')
}

/**
 * Shared status chip label + pill color so the table and mobile card agree. Shows the RAW status
 * (title-cased), with ONE curated exception: `draft` reads "Not submitted" (clinician-facing wording
 * for an unsubmitted ordering session). The `color` here is a Tailwind bg/text utility string for
 * any non-Badge consumer; the Badge itself uses `getWorkspaceStatusBadgeClass`. Empty/null → 'Unknown'.
 */
export function getWorkspaceStatusDisplay(status: string | null | undefined): { label: string; color: string } {
  const raw = (status ?? '').trim()
  if (!raw)
    return {
      label: 'Unknown',
      color: 'bg-background-secondary-medium dark:bg-gray-800 text-text-body dark:text-gray-400',
    }
  if (raw.toLowerCase() === 'draft')
    return {
      label: 'Not submitted',
      color: 'bg-background-secondary-medium dark:bg-gray-800 text-text-body dark:text-gray-400',
    }
  return {
    label: titleCaseStatus(raw),
    color: 'bg-background-secondary-medium dark:bg-gray-800 text-text-body dark:text-gray-400',
  }
}

/** Tailwind bg/text utility classes for the status chip. Unmapped/empty → neutral gray. */
export function getWorkspaceStatusBadgeClass(status: string | null | undefined): string {
  return statusBadgeClasses[(status ?? '').trim().toLowerCase()] ?? UNKNOWN_BADGE_CLASS
}

/**
 * Client-derived, safe action hint. Only known-safe cases (drafts) get a hint;
 * everything else returns null (no speculative guidance).
 */
export function getWorkspaceDraftActionHint(order: OrderStatusResponse): string | null {
  return order.status === 'draft' ? 'Finish order' : null
}

export function calculateAge(dob: string | undefined): string {
  if (!dob) return 'N/A'
  const birthDate = new Date(dob)
  const today = new Date()
  const diffMs = today.getTime() - birthDate.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays < 7) {
    return `${diffDays} d`
  }
  if (diffDays < 60) {
    const weeks = Math.floor(diffDays / 7)
    return `${weeks} w`
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30)
    return `${months} m`
  }
  const years = Math.floor(diffDays / 365)
  return `${years} y`
}

export function comparatorRelationshipLabel(rel: string): string {
  const key = rel.toLowerCase().trim().replace(/\s+/g, '_')
  const labels: Record<string, string> = {
    bio_mother: 'Bio mother',
    bio_father: 'Bio father',
    mother: 'Mother',
    father: 'Father',
  }
  return labels[key] ?? rel.replace(/_/g, ' ')
}

function rawTestingComparatorFlag(c: OrderComparatorSummary): boolean | undefined {
  return c.testing_comparator ?? (c as { testingComparator?: boolean }).testingComparator
}

/** Only explicit true counts as a sequencing comparator; missing/false = contact or guardian row. */
export function isTestingComparatorRow(c: OrderComparatorSummary): boolean {
  return rawTestingComparatorFlag(c) === true
}

export function isPrimaryGuardianComparatorRow(c: OrderComparatorSummary): boolean {
  const v = c.is_primary_guardian ?? (c as { isPrimaryGuardian?: boolean }).isPrimaryGuardian
  return v === true
}

/** Order # column: draft sessions have no assigned order id yet — show "Not submitted" label. */
export function workspaceOrderNumberDisplay(order: OrderStatusResponse): string {
  if (order.status === 'draft') {
    return 'Not submitted'
  }
  const ext = order.inflection_external_id?.trim()
  return ext || order.order_id
}

/**
 * MRN cell label. Backend values may already carry an "MRN" prefix
 * (e.g. "MRN624520547"), which caused a doubled "MRN MRN..." display. Strip any
 * leading MRN prefix (with optional separator) before re-applying a single one.
 */
export function formatMrnDisplay(mrn: string | null | undefined): string {
  const raw = mrn?.trim()
  if (!raw) return 'MRN ---'
  const stripped = raw.replace(/^mrn[\s:#-]*/i, '').trim()
  return `MRN ${stripped || '---'}`
}

export function formatShortDate(value: string | null | undefined): string {
  if (!value) return 'N/A'
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * GEN-5106: single source of truth for the Provider column's two-line display, shared by the
 * desktop table and mobile card so they can't drift.
 *
 * The ordering provider (provider_first/last_name) is the clinician who SUBMITTED the order. The
 * authorizing provider (authorizing_provider_first/last_name) is the responsible clinician the
 * order was placed on behalf of — populated ONLY when a distinct delegate submitted on someone
 * else's behalf (see CONTRACT.md). Whitespace-only names count as blank.
 *
 * - main = authorizing full name, else ordering full name, else 'N/A'.
 * - submittedBy = "Submitted by {ordering}" ONLY when a GENUINELY DISTINCT authorizing provider
 *   exists AND we have an ordering name to attribute the submission to; null means render no sub
 *   line. Distinctness is compared case-insensitively on the whitespace-normalized names, so the
 *   SPA never fabricates a self-attribution if it ever receives equal/duplicate strings (upstream
 *   imdb-client already only sends authorizing fields when distinct — this is defense-in-depth).
 */
export function getWorkspaceProviderDisplay(order: OrderStatusResponse): { main: string; submittedBy: string | null } {
  // Trim edges AND collapse internal whitespace runs so "Bob   Jones" renders as "Bob Jones".
  const fullName = (first?: string | null, last?: string | null): string =>
    `${first ?? ''} ${last ?? ''}`.trim().replace(/\s+/g, ' ')
  const ordering = fullName(order.provider_first_name, order.provider_last_name)
  const authorizing = fullName(order.authorizing_provider_first_name, order.authorizing_provider_last_name)
  const main = authorizing || ordering || 'N/A'
  // Only attribute a submitter when a DISTINCT delegate exists (different person) and we can name
  // them. Equal names (self-submit that slipped through) → one name, no fabricated sub line.
  const distinct = authorizing !== '' && ordering !== '' && authorizing.toLowerCase() !== ordering.toLowerCase()
  const submittedBy = distinct ? `Submitted by ${ordering}` : null
  return { main, submittedBy }
}
